import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, toApiSort } from "@/lib/utils";
import { setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { useDeleteVendorMutation, useGetCommunities, useGetVendorList } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, MoreVertical, Trash2 } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";

const useVendorMgmt = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "vendorId", desc: true }]);
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const communityId = isMasterData ? communityDetail?.communityId : id;

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  useEffect(() => {
    if (communityList?.data) {
      setCommunityKey(Math.random() * 3);
    }
  }, [communityList]);

  const {
    data: vendorList,
    isLoading: isVendorLoading,
    refetch: refetchVendorList,
  } = useGetVendorList({
    data: {
      filters: [
        {
          field: "communityId",
          value: communityId,
          operator: "EQUAL",
        },
      ],
      search: globalFilter,
      searchFields: [
        "vendorName",
        "vendorContactName",
        "vendorEmail",
        "updatedByUserName",
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "vendorId", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityId,
    },
  });

  useEffect(() => {
    refetchVendorList();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter.length >= 2 || globalFilter.length == 0) {
      refetchVendorList();
    }
  }, [globalFilter]);

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteVendorMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["vendors"],
        });
        setOpenDeleteDialog(false);
        setDeletedRow(null);
        toast.success("Vendor Deleted Successfully");
      },
    });

  const onAdd = () => {
    navigate("add");
  };

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
    setTimeout(() => refetchVendorList(), 100);
  };

  // Define Table Columns
  const columns = [
    {
      // Per-community number (starts at 1 in every community), not the table-wide id
      accessorKey: "displayId",
      header: "ID",
      accessorFn: (row: any) => String(row.displayId ?? ""),
    },
    {
      accessorKey: "vendorName",
      header: (
        <div className="w-full">
          Vendor <br /> Name
        </div>
      ),
      accessorFn: (row: any) => String(row?.vendorName || ""),
    },
    {
      accessorKey: "vendorContactName",
      header: (
        <div className="w-full">
          Contact <br /> Name
        </div>
      ),
      accessorFn: (row: any) => String(row?.vendorContactName || ""),
    },
    {
      accessorKey: "vendorPhone",
      header: (
        <div className=" w-full">
          Phone <br /> No.
        </div>
      ),
      accessorFn: (row: any) => String(row?.vendorPhone || ""),
    },
    {
      accessorKey: "vendorEmail",
      header: (
        <div className=" w-full">
          Email <br /> ID
        </div>
      ),
      accessorFn: (row: any) => String(row?.vendorEmail || ""),
    },
    {
      accessorKey: "active",
      header: (
        <div className="flex items-center w-[80px] justify-center gap-2">
          Status
        </div>
      ),
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              row.row.original.active
                ? "border-green-600 bg-green-100 text-green-600"
                : "border-red-600 bg-red-100 text-red-600",
              "rounded-full px-4 py-2"
            )}
          >
            {row.row.original.active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "updatedAt",
      header: (
        <div className=" w-full">
          Last <br /> Updated On
        </div>
      ),
      accessorFn: (row: any) =>
        row?.updatedAt ? moment(row?.updatedAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "updatedByUserName",
      header: (
        <div className=" w-full">
          Last <br /> Updated By
        </div>
      ),
      accessorFn: (row: any) => String(row?.updatedByUserName || ""),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigate(`${row.row.original.vendorId}/edit`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDeletedRow(row.row.original);
                  setOpenDeleteDialog(true);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.vendorId}/view`);
  };

  const onDeleteConfirm = () => {
    deleteInfo({ id: deletedRow?.vendorId as string });
  };

  return {
    isMobile,
    globalFilter,
    setGlobalFilter,
    onAdd,
    vendorList,
    isVendorLoading,
    columns,
    onRowClick,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeletePending,
    onDeleteConfirm,
    deletedRow,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    isMasterDataRoute: isMasterData,
    communityList,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    communityKey,
  };
};

export default useVendorMgmt;
